import React, { useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Trash2, MessageSquare, Calendar, User, ChevronUp, ChevronDown, FileText, Zap, Bell, BarChart, FolderKanban } from "lucide-react";
import { useSortableTable, SortField } from "@/hooks/useSortableTable";
import UserStatusBadge from "./UserStatusBadge";
import SendNotificationModal from "./SendNotificationModal";
import UserDetailsModal from "./UserDetailsModal";
import ExportUsersButtons from "./ExportUsersButtons";
interface User {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
  status_chat: boolean | null;
  last_access: string | null;
  casos_count: number;
  conversations_count: number;
  documents_count: number;
  tokens_input: number;
  tokens_output: number;
  total_tokens: number;
  limit_tokens: number;
}

interface UsersTableProps {
  users: User[];
  getSubscriptionForUser: (userId: string) => any;
  getUserSessionInfo: (userId: string) => any;
  formatDate: (date: string) => string;
  formatStorageLimit: (bytes: number) => string;
  onUpdateStorageLimit: (userId: string, planType: 'free' | 'basic' | 'pro' | 'premium') => Promise<void>;
  onToggleApproval: (userId: string, currentStatus: boolean) => Promise<boolean>;
  onDeleteUser: (userId: string) => Promise<void>;
}

const UsersTable = ({
  users,
  getSubscriptionForUser,
  getUserSessionInfo,
  formatDate,
  formatStorageLimit,
  onUpdateStorageLimit,
  onToggleApproval,
  onDeleteUser
}: UsersTableProps) => {
  const { sortedData, sortField, sortDirection, toggleSort } = useSortableTable({
    data: users,
    getSubscriptionForUser
  });

  // Paginação: 25 usuários por página
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Calcular dados paginados
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  // Reset para página 1 quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [sortedData.length]);

  // Estado para o modal de notificação
  const [notificationModal, setNotificationModal] = useState({
    open: false,
    userId: '',
    userEmail: '',
    displayName: ''
  });

  // Estado para o modal de detalhes do usuário
  const [userDetailsModal, setUserDetailsModal] = useState({
    open: false,
    userId: '',
    userEmail: '',
    displayName: '',
    userInfo: null as any,
    subscriptionInfo: null as any,
    sessionInfo: null as any
  });

  const handleToggleApproval = async (userId: string, currentStatus: boolean) => {
    await onToggleApproval(userId, currentStatus);
  };

  const formatLastAccess = (userId: string, userCreatedAt: string) => {
    console.log(`🔍 formatLastAccess chamado para userId: ${userId}`);
    const sessionInfo = getUserSessionInfo(userId);
    console.log(`📋 sessionInfo retornado:`, sessionInfo);
    
    if (!sessionInfo?.last_active) {
      console.log(`❌ Sem last_active para userId: ${userId}, exibindo "Nunca esteve online"`);
      return "Nunca esteve online";
    }
    
    const date = new Date(sessionInfo.last_active);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return "Há poucos minutos";
    if (diffInHours < 24) return `Há ${diffInHours}h`;
    if (diffInHours < 48) return "Ontem";
    if (diffInHours < 168) return `Há ${Math.floor(diffInHours / 24)} dias`;
    
    return formatDate(sessionInfo.last_active);
  };

  // CORREÇÃO 3: Função para obter limite de tokens correto baseado no plano
  const getTokenLimitForUser = (userId: string) => {
    const subscription = getSubscriptionForUser(userId);
    const planType = subscription?.plan_type || 'free';
    
    // Definir limites baseados no plano
    switch (planType) {
      case 'premium':
        return 2000000; // 2M
      case 'pro':
        return 1000000; // 1M
      case 'basic':
        return 500000; // 500K
      case 'free':
      default:
        return 10000; // 10K
    }
  };

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = sortField === field;
    const Icon = isActive && sortDirection === 'desc' ? ChevronDown : ChevronUp;
    
    return (
      <TableHead 
        className="cursor-pointer hover:bg-muted/50 select-none transition-colors"
        onClick={() => toggleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <Icon className={`h-4 w-4 transition-opacity ${isActive ? 'opacity-100' : 'opacity-30'}`} />
        </div>
      </TableHead>
    );
  };

  return (
    <div className="space-y-4">
      {/* CONTROLES DE PAGINAÇÃO E EXPORTAÇÃO NO TOPO */}
      <div className="flex items-center justify-between px-2 flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="text-sm text-muted-foreground">
            Mostrando {startIndex + 1}-{Math.min(endIndex, sortedData.length)} de {sortedData.length} usuários
          </div>
          
          {/* Botões de Exportação */}
          <ExportUsersButtons
            users={sortedData}
            getSubscriptionForUser={getSubscriptionForUser}
            getUserSessionInfo={getUserSessionInfo}
          />
        </div>
        
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <PaginationItem key={page}>
                <PaginationLink
                  onClick={() => setCurrentPage(page)}
                  isActive={currentPage === page}
                  className="cursor-pointer"
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}
            
            <PaginationItem>
              <PaginationNext
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>

      {/* TABELA */}
      <div className="rounded-md border overflow-x-auto">
        <Table className="min-w-[1400px]">
        <TableHeader>
          <TableRow>
            <SortableHeader field="user">Usuário</SortableHeader>
            <TableHead>Status</TableHead>
            <SortableHeader field="lastAccess">Último Acesso</SortableHeader>
            <SortableHeader field="casos">Casos</SortableHeader>
            <SortableHeader field="conversations">Conversas</SortableHeader>
            <TableHead>Documentos</TableHead>
            <TableHead>Tokens Usados</TableHead>
            <SortableHeader field="plan">Plano</SortableHeader>
            <SortableHeader field="storage">Armazenamento</SortableHeader>
            <SortableHeader field="approved">Chat</SortableHeader>
            <TableHead>Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedData.map((user) => {
            console.log(`🔍 Renderizando usuário: ${user.email} (ID: ${user.id})`);
            
            const subscription = getSubscriptionForUser(user.id);
            const sessionInfo = getUserSessionInfo(user.id);
            const planType = subscription?.plan_type || 'free';
            const isOnline = sessionInfo?.is_online || false;
            
            // CORREÇÃO 3: Usar limite correto baseado no plano ao invés de user.limit_tokens
            const tokenLimit = getTokenLimitForUser(user.id);
            
            console.log(`📊 Dados do usuário ${user.email}:`, {
              userId: user.id,
              sessionInfo: sessionInfo,
              isOnline: isOnline,
              hasSessionInfo: !!sessionInfo,
              planType: planType,
              tokenLimit: tokenLimit
            });
            
            return (
              <TableRow key={user.id}>
                <TableCell className="min-w-[180px]">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p 
                          className="font-medium cursor-pointer hover:text-primary transition-colors"
                          onClick={() => setUserDetailsModal({
                            open: true,
                            userId: user.id,
                            userEmail: user.email,
                            displayName: user.display_name || '',
                            userInfo: user,
                            subscriptionInfo: subscription,
                            sessionInfo: sessionInfo
                          })}
                        >
                          {user.display_name || 'Sem nome'}
                        </p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                  </div>
                </TableCell>
                
                <TableCell>
                  <UserStatusBadge isOnline={isOnline} />
                </TableCell>
                
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                     <span className="text-sm">
                       {formatLastAccess(user.id, user.created_at)}
                     </span>
                  </div>
                </TableCell>
                
                <TableCell>
                  <div className="flex items-center gap-2">
                    <FolderKanban className="h-4 w-4 text-muted-foreground" />
                    <Badge variant="secondary">
                      {user.casos_count}
                    </Badge>
                  </div>
                </TableCell>
                
                <TableCell>
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                    <Badge variant="secondary">
                      {user.conversations_count}
                    </Badge>
                  </div>
                </TableCell>
                
                <TableCell>
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <Badge variant="outline">
                      {user.documents_count}
                    </Badge>
                  </div>
                </TableCell>
                
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-muted-foreground" />
                    <div className="space-y-1 min-w-[100px]">
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-medium">
                          {Math.round(user.total_tokens / 1000)}K/{Math.round(tokenLimit / 1000)}K
                        </span>
                        <span className="text-xs text-muted-foreground">
                          ({Math.round((user.total_tokens / tokenLimit) * 100)}%)
                        </span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-colors ${
                            (user.total_tokens / tokenLimit) >= 1.0 ? 'bg-destructive' :
                            (user.total_tokens / tokenLimit) >= 0.8 ? 'bg-yellow-500' : 'bg-green-500'
                          }`}
                          style={{
                            width: `${Math.min((user.total_tokens / tokenLimit) * 100, 100)}%`,
                          }}
                        />
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Entrada: {Math.round(user.tokens_input / 1000)}K • Saída: {Math.round(user.tokens_output / 1000)}K
                      </div>
                    </div>
                  </div>
                </TableCell>
                
                <TableCell>
                  <Select
                    value={planType}
                    onValueChange={(value: 'free' | 'basic' | 'pro' | 'premium') =>
                      onUpdateStorageLimit(user.id, value)
                    }
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">Gratuito</SelectItem>
                      <SelectItem value="basic">Básico</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                
                <TableCell className="min-w-[140px]">
                  <div className="space-y-1">
                    <p className="text-xs">
                      {formatStorageLimit(subscription?.current_usage || 0)} /{" "}
                      {formatStorageLimit(subscription?.storage_limit || 0)}
                    </p>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{
                          width: `${Math.min(
                            ((subscription?.current_usage || 0) /
                              (subscription?.storage_limit || 1)) *
                              100,
                            100
                          )}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </TableCell>
                
                <TableCell>
                  <Switch
                    checked={user.status_chat !== false}
                    onCheckedChange={() =>
                      handleToggleApproval(user.id, user.status_chat !== false)
                    }
                  />
                </TableCell>
                
                <TableCell>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setNotificationModal({
                        open: true,
                        userId: user.id,
                        userEmail: user.email,
                        displayName: user.display_name || ''
                      })}
                    >
                      <Bell className="h-4 w-4" />
                    </Button>
                    
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir usuário</AlertDialogTitle>
                          <AlertDialogDescription>
                            Tem certeza que deseja excluir o usuário "{user.email}"? 
                            Esta ação não pode ser desfeita e removerá todos os dados 
                            associados ao usuário.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => onDeleteUser(user.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Excluir
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      </div>
      
      {/* Modal para envio de notificação */}
      <SendNotificationModal
        open={notificationModal.open}
        onOpenChange={(open) => setNotificationModal(prev => ({ ...prev, open }))}
        userId={notificationModal.userId}
        userEmail={notificationModal.userEmail}
        displayName={notificationModal.displayName}
      />

      {/* Modal para detalhes do usuário */}
      <UserDetailsModal
        open={userDetailsModal.open}
        onOpenChange={(open) => setUserDetailsModal(prev => ({ ...prev, open }))}
        userId={userDetailsModal.userId}
        userEmail={userDetailsModal.userEmail}
        displayName={userDetailsModal.displayName}
        userInfo={userDetailsModal.userInfo}
        subscriptionInfo={userDetailsModal.subscriptionInfo}
        sessionInfo={userDetailsModal.sessionInfo}
        formatDate={formatDate}
      />
    </div>
  );
};

export default UsersTable;
